// IndexedDB utility for offline data storage and sync
const DB_NAME = 'ToolTrackerOfflineDB';
const DB_VERSION = 1;

const STORES = {
  tools: 'tools',
  collaborators: 'collaborators',
  assignments: 'assignments',
  rentals: 'rentals',
  kits: 'kits',
  pendingSync: 'pendingSync',
  metadata: 'metadata'
};

let db = null;

// Initialize IndexedDB
export async function initDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create object stores
      if (!database.objectStoreNames.contains(STORES.tools)) {
        database.createObjectStore(STORES.tools, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.collaborators)) {
        database.createObjectStore(STORES.collaborators, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.assignments)) {
        database.createObjectStore(STORES.assignments, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.rentals)) {
        database.createObjectStore(STORES.rentals, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.kits)) {
        database.createObjectStore(STORES.kits, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.pendingSync)) {
        const syncStore = database.createObjectStore(STORES.pendingSync, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!database.objectStoreNames.contains(STORES.metadata)) {
        database.createObjectStore(STORES.metadata, { keyPath: 'key' });
      }
    };
  });
}

// Generic CRUD operations
export async function saveData(storeName, data) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    if (Array.isArray(data)) {
      data.forEach(item => store.put(item));
    } else {
      store.put(data);
    }

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getData(storeName, id = null) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = id ? store.get(id) : store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteData(storeName, id) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

export async function clearStore(storeName) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

// Pending sync operations for offline mutations
export async function addPendingSync(operation) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.pendingSync, 'readwrite');
    const store = transaction.objectStore(STORES.pendingSync);
    const request = store.add({
      ...operation,
      timestamp: Date.now()
    });

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingSyncs() {
  return getData(STORES.pendingSync);
}

export async function removePendingSync(id) {
  return deleteData(STORES.pendingSync, id);
}

export async function clearPendingSyncs() {
  return clearStore(STORES.pendingSync);
}

// Metadata operations
export async function setMetadata(key, value) {
  return saveData(STORES.metadata, { key, value, updatedAt: Date.now() });
}

export async function getMetadata(key) {
  const data = await getData(STORES.metadata, key);
  return data?.value;
}

// Store name exports
export { STORES };

// Sync all data from server to IndexedDB
export async function syncFromServer(apiCall, storeName) {
  try {
    const data = await apiCall();
    if (data && Array.isArray(data)) {
      await clearStore(storeName);
      await saveData(storeName, data);
      await setMetadata(`${storeName}_lastSync`, Date.now());
    }
    return data;
  } catch (error) {
    console.error(`Error syncing ${storeName}:`, error);
    // Return cached data if available
    return getData(storeName);
  }
}
